<script lang="tsx" setup>
import { ref, watch } from "vue"
import { useRoute } from "vue-router"

import { fetchStudentPreviews } from "@/store/projects"
import { useSearch, type SearchEntry } from "@/composables/use-search"
import { useSearchIndex } from "@/composables/use-search-index"
import {
  studentPagePreviewQuery,
  type KirbyStudentPreviewData,
} from "@/queries/student"

import SearchModal from "@/components/SearchModal/SearchModal.vue"
import ProjectGrid from "@/components/Projects/ProjectGrid.vue"

const route = useRoute()

// ─── Build the search index ─────────────────────────────────────────────────
// Shared corpus (students + programmes) — see use-search-index.ts. Awaiting it
// here lets Suspense hold the panel until the index is ready; SearchModal's
// typeahead consumes the same cached entries.
const { ensureIndex } = useSearchIndex()
const entries = await ensureIndex()

const { fuseResults, search } = useSearch(entries)

// ─── Results ────────────────────────────────────────────────────────────────
// Student entries carry name/title; programme entries carry only `programme`,
// so the field tells us which bucket a Fuse hit belongs to. Student hits are
// hydrated with their thumbnail data so they can render as StudentItem cards.
const programmeResults = ref<SearchEntry[]>([])
const studentResults = ref<KirbyStudentPreviewData[]>([])
const isSearching = ref(false)

const runSearch = async (query: string | undefined) => {
  if (!query) {
    programmeResults.value = []
    studentResults.value = []
    return
  }

  isSearching.value = true
  search(query) // populates fuseResults synchronously

  programmeResults.value = fuseResults.value
    .filter(result => !!result.item.programme)
    .map(result => result.item)

  const studentIds = fuseResults.value
    .filter(result => result.item.name || result.item.title)
    .map(result => result.item.id)

  const hydrated = await fetchStudentPreviews(
    studentIds,
    studentPagePreviewQuery,
  )
  // Preserve Fuse relevance order (site.find order isn't guaranteed).
  const byId = new Map(hydrated.map(student => [student.id, student]))
  studentResults.value = studentIds
    .map(id => byId.get(id))
    .filter((s): s is KirbyStudentPreviewData => Boolean(s))

  isSearching.value = false
}

watch(() => route.query.q as string | undefined, runSearch, { immediate: true })
</script>

<template>
  <div class="px-4 pb-12">
    <!-- Refine input: reuses the menu search input, prefilled from ?q= -->
    <div class="pt-1 mb-6 rounded border">
      <SearchModal />
    </div>

    <!-- Programmes -->
    <div v-if="programmeResults.length" class="mb-8">
      <div class="text-sm underline mb-linebreak">Programmes</div>
      <div class="flex flex-col gap-px">
        <RouterLink
          v-for="programme in programmeResults"
          :key="programme.id"
          :to="`/${programme.uri}`"
          class="link"
          v-html="programme.programme"
        />
      </div>
    </div>

    <!-- Students / projects -->
    <ProjectGrid v-if="studentResults.length" :students="studentResults">
      Projects
    </ProjectGrid>

    <!-- Empty state -->
    <div
      v-if="
        route.query.q &&
        !isSearching &&
        !programmeResults.length &&
        !studentResults.length
      "
      class="text-sm opacity-50"
    >
      No results for “{{ route.query.q }}”.
    </div>
  </div>
</template>
